import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { User } from '../interfaces/user.model';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { StorageService } from '../../services/storage.service';
import { IRegisterReq, IRegisterResult } from '../interfaces/register.interface';
import { IUserLoginSuccess } from '../interfaces/login.interface';
import { JwtHelperService } from '@auth0/angular-jwt';
import { IUser } from 'src/app/core/interfaces/core.interfaces'
import { USER_TYPES } from 'src/app/core/constants';
import { IResponse } from '../../interfaces/response.interface';
import { GleapService } from '../../services/gleap.service';
import { Gleap } from 'capacitor-gleap-plugin';
const baseUrl = `${environment.apiUrl}auth`;

@Injectable({ providedIn: 'root' })
export class AuthService {
	private userSubject = new BehaviorSubject<User | null>(null);
	public user = this.userSubject.asObservable();

	constructor(
		private router: Router,
		private http: HttpClient,
		private readonly jwtHelper: JwtHelperService,
		private storageService: StorageService,
		private gleapService: GleapService
	) { }

	public get userValue(): User | null {
		return this.userSubject.value;
	}

	public get userName(): string | undefined {
		const user = this.currentUserFromToken;
		return (user.type == 'person') ? user.firstName : user.companyName

	}

	public get currentUserFromToken(): IUser {
		const token = this.token;
		return token ? this.decodeUserFromToken(token) : {} as IUser;
	}

	public get token(): string | null {
		return this.storageService.get('token');
	}

	public get isLoggedIn(): boolean {
		return !!this.token && !this.jwtHelper.isTokenExpired(this.token);
	}

	private decodeUserFromToken(token: string): IUser {
		return this.jwtHelper.decodeToken(token) ?? {} as IUser;
	}

	login(email: string, password: string): Observable<IUser> {
		return this.http.post<IUserLoginSuccess>(`${baseUrl}/sign-in`, { email, password })
			.pipe(map(response => {
				const token = response.data.token;
				this.setToken(token);
				const user = this.decodeUserFromToken(token);
				Gleap.identify({
					userId: user.id ?? '',
					name: (user.companyName) ? user.companyName : `${user.firstName} ${user.lastName}`,
					email: user.email,
					companyName: user.companyName,
				});

				this.userSubject.next(user);
				return user;
			}));
	}

	register(user: IRegisterReq): Observable<IRegisterResult> {
		return this.http.post<IResponse>(`${baseUrl}/sign-up`, { ...user })
			.pipe(map(response => response.data));
	}

	validateAccount(code: string): Observable<any> {
		return this.http.get<any>(`${baseUrl}/validate?code=${code}`)
			.pipe(map(response => response.data));
	}

	resetPassword(email: string): Observable<any> {
		return this.http.post<any>(`${baseUrl}/password-reset/send-email`, { email })
			.pipe(map(response => response.data));
	}

	recoverPassword(code: string, password: string, passwordConfirm: string, email: string): Observable<any> {
		return this.http.post<any>(`${baseUrl}/recover`, {
			code,
			password,
			password_confirm: passwordConfirm,
			email
		}).pipe(map(response => response.data));
	}

	setToken(token: string): void {
		this.storageService.set('token', token);
	}

	logout(): void {
		localStorage.setItem('token', '');
		localStorage.clear();
		this.userSubject.next(null);
		this.router.navigateByUrl('/auth/login');
	}
}